/**
 * The plan summary's cost block, folded (owner, 22 Sep 2026): one number always — the daily cost and
 * its yearly figure — and the fee lines behind "See how it is made up". The lines keep their
 * calculation tooltips (an adviser hovers a line for the working behind it), which is the part that
 * must never go missing when the block is folded.
 */
import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { formatCurrency } from '../../utils/index.ts';

interface CostOverviewDisclosureProps {
    dailyTotal: number;
    /** The fee lines (SummaryRow elements), shown when opened. */
    children: React.ReactNode;
    /** Open on first render — the rail's default is closed. */
    defaultOpen?: boolean;
    /** One line under the figure — the second stage, where a second person enters care later. */
    note?: string;
}

const CostOverviewDisclosure = ({ dailyTotal, children, defaultOpen = false, note }: CostOverviewDisclosureProps) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="bg-gray-800 text-white rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
                <p className="text-gray-400 text-[13px] leading-snug">Your estimated<br />daily cost</p>
                <div className="text-right">
                    <p className="text-3xl font-bold text-white leading-none">{formatCurrency(dailyTotal)}</p>
                    <p className="text-gray-300 font-semibold text-[13px] mt-1">{formatCurrency((dailyTotal || 0) * 365)} / year</p>
                </div>
            </div>
            {note && <p className="mt-2 text-[13px] leading-snug text-sky-200" data-testid="cost-phase-two">{note}</p>}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="mt-3 flex w-full items-center justify-between gap-2 rounded-lg border border-white/15 px-3 py-2 text-[13px] font-semibold text-sky-200 hover:bg-white/5"
            >
                <span>{open ? 'Hide how it is made up' : 'See how it is made up'}</span>
                <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="mt-3 space-y-3 border-t border-white/15 pt-3">
                    {children}
                    <p className="text-[13px] text-gray-400">Hover a line for the calculation behind it.</p>
                </div>
            )}
        </div>
    );
};

export default CostOverviewDisclosure;
